<template>
  <div class="py-4 container-fluid">
    <div class="row">
      <div class="col-lg-12 mb-4">
        <h2 class="text-white">Bienvenido {{ user.name }}</h2>
        <p class="text-white text-sm">Panel de métricas de ventas</p>
      </div>

      <!-- Tarjetas de métricas -->
      <div class="col-lg-3 col-md-6 col-12" v-if="!loading">
        <card :title="stats.sales?.title ?? 'Ventas Hoy'" :value="stats.sales?.value ?? '0'"
          :detail="stats.sales?.detail"
          :iconClass="stats.sales?.iconClass ?? 'ni ni-cart'" :iconBackground="stats.sales?.iconBackground ?? 'bg-gradient-success'" directionReverse>
        </card>
      </div>
      <div class="col-lg-3 col-md-6 col-12" v-if="!loading">
        <card :title="stats.month_sales?.title ?? 'Ventas del Mes'" :value="stats.month_sales?.value ?? '0'"
          :detail="stats.month_sales?.detail"
          :iconClass="stats.month_sales?.iconClass ?? 'ni ni-chart-bar-32'" :iconBackground="stats.month_sales?.iconBackground ?? 'bg-gradient-warning'" directionReverse>
        </card>
      </div>
      <div class="col-lg-3 col-md-6 col-12" v-if="!loading">
        <card :title="stats.total_sales?.title ?? 'Total Ventas'" :value="stats.total_sales?.value ?? '0'"
          :detail="stats.total_sales?.detail"
          :iconClass="stats.total_sales?.iconClass ?? 'ni ni-bag-17'" :iconBackground="stats.total_sales?.iconBackground ?? 'bg-gradient-info'" directionReverse>
        </card>
      </div>
      <div class="col-lg-3 col-md-6 col-12" v-if="!loading">
        <card :title="stats.pending_balance?.title ?? 'Pendiente por Pagar'" :value="stats.pending_balance?.value ?? '0'"
          :detail="stats.pending_balance?.detail"
          :iconClass="stats.pending_balance?.iconClass ?? 'ni ni-money-coins'" :iconBackground="stats.pending_balance?.iconBackground ?? 'bg-gradient-danger'" directionReverse>
        </card>
      </div>

      <div v-if="loading" class="col-12 text-center py-5">
        <i class="fas fa-spinner fa-spin fa-2x text-primary"></i>
        <p class="mt-2 text-sm text-secondary">Cargando métricas...</p>
      </div>

      <!-- Tarjeta copiar enlace catálogo -->
      <div class="col-lg-3 col-md-6 col-12" v-if="!loading">
        <div class="mb-4 card">
          <div class="p-3 card-body">
            <div class="d-flex w-100 align-items-center flex-row-reverse justify-content-between">
              <div>
                <div class="text-center icon icon-shape bg-gradient-primary border-radius-2xl">
                  <i class="fas fa-share-alt text-lg opacity-10"></i>
                </div>
              </div>
              <div class="numbers">
                <p class="mb-0 text-sm text-uppercase font-weight-bold">Catálogo</p>
                <h5 class="font-weight-bolder text-primary cursor-pointer" @click="copyCatalogLink">
                  {{ catalogLink ? 'Copiar enlace' : 'Generar enlace' }}
                </h5>
                <span class="text-sm text-muted">Compartir con clientes</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Métodos de pago y productos más vendidos -->
      <template v-if="!loading">
        <div class="col-lg-6 mb-lg-0 mb-4 mt-4">
          <div class="card">
            <div class="p-3 pb-0 card-header">
              <div class="d-flex justify-content-between">
                <h6 class="mb-2">Métodos de Pago</h6>
              </div>
            </div>
            <div class="table-responsive" style="overflow: auto; max-height: 500px;">
              <table class="table align-items-center">
                <thead>
                  <tr>
                    <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Método</th>
                    <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Transacciones</th>
                    <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Total</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(value, index) in paymentMethods" :key="index">
                    <td>
                      <div class="px-2 py-1 d-flex align-items-center">
                        <div class="ms-4">
                          <h6 class="mb-0 text-sm">{{ value.method }}</h6>
                        </div>
                      </div>
                    </td>
                    <td class="text-center text-sm">
                      <span class="badge badge-sm bg-gradient-info">{{ value.count }}</span>
                    </td>
                    <td class="text-center text-sm">
                      <span class="font-weight-bold">{{ formatCurrency(value.total) }}</span>
                    </td>
                  </tr>
                  <tr v-if="paymentMethods.length === 0">
                    <td class="text-center" colspan="3">
                      <p>No hay registros</p>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div class="col-lg-6 mb-lg-0 mb-4 mt-4">
          <div class="card">
            <div class="p-3 pb-0 card-header">
              <div class="d-flex justify-content-between">
                <h6 class="mb-2">Productos Más Vendidos</h6>
              </div>
            </div>
            <div class="table-responsive" style="overflow: auto; max-height: 500px;">
              <table class="table align-items-center">
                <thead>
                  <tr>
                    <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Producto</th>
                    <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Cantidad</th>
                    <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Total</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(value, index) in topProducts" :key="index">
                    <td>
                      <div class="px-2 py-1 d-flex align-items-center">
                        <div class="ms-4">
                          <h6 class="mb-0 text-sm">{{ value.name }}</h6>
                          <p class="mb-0 text-xs text-secondary">{{ value.ref ?? '—' }}</p>
                        </div>
                      </div>
                    </td>
                    <td class="text-center text-sm">
                      <span class="badge badge-sm bg-gradient-success">{{ value.quantity }}</span>
                    </td>
                    <td class="text-center text-sm">
                      <span class="font-weight-bold">{{ formatCurrency(value.total) }}</span>
                    </td>
                  </tr>
                  <tr v-if="topProducts.length === 0">
                    <td class="text-center" colspan="3">
                      <p>No hay registros</p>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import Card from '@/examples/Cards/Card.vue';
import Swal from 'sweetalert2';
import catalogService from '@/services/catalog.service';

export default {
  name: 'OperadorDashboard',
  components: { Card },
  data() {
    return {
      user: {},
      loading: true,
      stats: {
        sales: { title: 'Ventas Hoy', value: '0', iconClass: 'ni ni-cart', iconBackground: 'bg-gradient-success', detail: 'Abonado del día' },
        month_sales: { title: 'Ventas del Mes', value: '0', iconClass: 'ni ni-chart-bar-32', iconBackground: 'bg-gradient-warning', detail: 'Abonado del mes' },
        total_sales: { title: 'Total Ventas', value: '0', iconClass: 'ni ni-bag-17', iconBackground: 'bg-gradient-info', detail: 'Total abonado' },
        pending_balance: { title: 'Pendiente por Pagar', value: '0', iconClass: 'ni ni-money-coins', iconBackground: 'bg-gradient-danger', detail: 'Saldos pendientes' },
      },
      paymentMethods: [],
      topProducts: [],
      catalogLink: '',
    };
  },
  methods: {
    formatCurrency(value) {
      return parseFloat(value || 0).toLocaleString('es-ES', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      });
    },
    async copyCatalogLink() {
      try {
        if (!this.catalogLink) {
          const res = await catalogService.getMyLink();
          this.catalogLink = res.data.url;
        }
        await navigator.clipboard.writeText(this.catalogLink);
        await Swal.fire({
          icon: 'success',
          title: 'Enlace copiado',
          text: 'Pégalo y compártelo con tus clientes.',
          timer: 2000,
          showConfirmButton: false,
        });
      } catch (e) {
        console.error('Error al copiar enlace:', e);
        await Swal.fire('Error', 'No se pudo copiar el enlace.', 'error');
      }
    },
    async fetchSellerCards() {
      this.loading = true;
      try {
        const user = JSON.parse(localStorage.getItem('user'));
        if (!user) return;
        const response = await this.$store.dispatch('analitycs/getSellerCards', user.id);
        const data = response.data;
        this.stats = {
          sales: data?.sales || this.stats.sales,
          month_sales: data?.month_sales || this.stats.month_sales,
          total_sales: data?.total_sales || this.stats.total_sales,
          pending_balance: data?.pending_balance || this.stats.pending_balance,
        };
        this.paymentMethods = Array.isArray(data?.payment_methods) ? data.payment_methods : [];
        this.topProducts = Array.isArray(data?.top_products) ? data.top_products : [];
      } catch (error) {
        console.error('Error al cargar métricas del vendedor:', error);
      } finally {
        this.loading = false;
      }
    },
  },
  mounted() {
    const user = JSON.parse(localStorage.getItem('user'));
    if (user) {
      this.user = user;
    }
    this.fetchSellerCards();
  },
};
</script>

<style scoped>
.card {
  border-radius: 0.75rem;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
}
</style>
